// Hook for centralized minimum order amount logic
// Respects discount code's minOrderOverride when present

import { useCartStore } from '../store/cartStore';
import { MINIMUM_ORDER_AMOUNT } from '../shared/constants';

interface UseMinimumOrderResult {
    /** The effective minimum order amount (considering discount override) */
    effectiveMinimum: number;
    /** Whether the current cart total meets the minimum requirement */
    meetsMinimum: boolean;
    /** Formatted minimum amount text (e.g., "£5.00") or null if minimum is 0 */
    minimumText: string | null;
    /** Whether there are items in the cart */
    hasItems: boolean;
}

/**
 * Centralized hook for minimum order amount logic.
 * Automatically respects discount code's minOrderOverride when present.
 * 
 * Usage:
 * ```tsx
 * const { effectiveMinimum, meetsMinimum, minimumText, hasItems } = useMinimumOrder();
 * ```
 */
export function useMinimumOrder(): UseMinimumOrderResult {
    const { cart } = useCartStore();
    
    // Use discount's minOrderOverride if defined, otherwise fall back to global minimum
    const effectiveMinimum = cart.discount?.minOrderOverride !== undefined
        ? cart.discount.minOrderOverride
        : MINIMUM_ORDER_AMOUNT;
    
    const meetsMinimum = cart.totals.total >= effectiveMinimum;
    
    // Format minimum text, return null if minimum is 0 (free order allowed)
    const minimumText = effectiveMinimum === 0
        ? null
        : `£${effectiveMinimum.toFixed(2)}`;
    
    const hasItems = cart.items.length > 0;
    
    return {
        effectiveMinimum,
        meetsMinimum,
        minimumText,
        hasItems,
    };
}
